import { useEffect, useState } from "react";
import { TFunction } from "i18next";
import { Flex, Modal, Stack } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useMask } from "@react-input/mask";

import {
  Button,
  CheckBox,
  Link,
  SectionLabel,
  SectionText,
  Text,
  TextInput,
} from "components";
import { AppConstants } from "../../../constants";
import classes from "./index.module.css";
import { trackEvent } from "utils/metaPixel";

const { CREDIT_REQUEST_FORM_PADDING, MASK_SWISS_PHONE_NUMBER } = AppConstants;

interface CreditRequestPersonalInfoProps {
  form: any;
  t: TFunction;
}

const CreditRequestPersonalInfo = ({
  form,
  t,
}: CreditRequestPersonalInfoProps) => {
  const [termsAndConditions, setTermsAndConditions] = useState<boolean>(false);
  const [opened, { open, close }] = useDisclosure(false);
  const [initialFormCheckout, setInitialFormCheckout] = useState(false);
  const [hasTracked, setHasTracked] = useState(false);

  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });

  useEffect(() => {
    if (initialFormCheckout && !hasTracked) {
      trackEvent("InitiateCheckout");
      setHasTracked(true);
    }
  }, [initialFormCheckout, hasTracked]);

  return (
    <>
      <Modal.Root
        opened={opened}
        size="lg"
        onClose={close}
        className={classes.modal}
        centered
      >
        <Modal.Overlay />
        <Modal.Content className={classes.content}>
          <Modal.Header>
            <Modal.Title className={classes.title}>
              {t("credit.basic.information.privacyPolicy.title")}
            </Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body pt={20}>
            <Text>
              {t("credit.basic.information.privacyPolicy.description")}
            </Text>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
      <Stack pb={CREDIT_REQUEST_FORM_PADDING} px={CREDIT_REQUEST_FORM_PADDING}>
        <SectionLabel
          label={t("credit.basic.information.user.title")}
          variant={"green"}
        />
        <TextInput
          label={t("input.firstName.label")}
          placeholder={t("input.firstName.placeholder")}
          key={form.key("firstName")}
          onInputFocus={setInitialFormCheckout}
          {...form.getInputProps("firstName")}
        />
        <TextInput
          label={t("input.lastName.label")}
          placeholder={t("input.lastName.placeholder")}
          key={form.key("lastName")}
          onInputFocus={setInitialFormCheckout}
          {...form.getInputProps("lastName")}
        />
        <TextInput
          label={t("input.phoneNumber.label")}
          placeholder={t("input.phoneNumber.placeholder")}
          key={form.key("phoneNumber")}
          {...form.getInputProps("phoneNumber")}
          type={"tel"}
          ref={phoneNumberRef}
          onInputFocus={setInitialFormCheckout}
          onBeforeInput={(event: any) => {
            const cursorPos = event.currentTarget.selectionStart || 0;
            if (cursorPos === 0) {
              const inputValue = (event as any).data;
              if (inputValue && inputValue !== "7") {
                event.preventDefault();
              }
            }
          }}
        />
        <TextInput
          label={t("input.email.label")}
          placeholder={t("input.email.placeholder")}
          key={form.key("email")}
          onInputFocus={setInitialFormCheckout}
          {...form.getInputProps("email")}
        />
        <Flex align={"center"}>
          <CheckBox
            variant="outline"
            checked={termsAndConditions}
            onChange={() => setTermsAndConditions((old) => !old)}
          />
          <Flex ml={"xs"}>
            <SectionText
              text={t("credit.basic.information.user.checkbox.text")}
              variant="dark"
            ></SectionText>
            <Link c="#0c4076" underline="always" fw={300} onClick={open}>
              {t("credit.basic.information.user.checkbox.link")}
            </Link>
          </Flex>
        </Flex>
        <Button
          type={"submit"}
          disabled={!termsAndConditions}
          style={{ alignSelf: "flex-start" }}
        >
          {t("action.checkApplication")}
        </Button>
      </Stack>
    </>
  );
};

export default CreditRequestPersonalInfo;
